import {
  base_colorContentBorder,
  text_colorTextPrimary,
  text_colorTextSecondary,
  text_colorTextSuccess,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import { font_family_weight_medium, font_md } from 'ext-ui/style/typography'
import { css } from 'styled-components'

/**
 * Vendors page (Bill Pay M2). Mirrors the prototype's page + VendorsEmptyState
 * (extend-design-render/invoice-pay-prototype, app/vendors/page.tsx): header
 * row with the title + always-visible primary CTA; empty state = centered
 * column, 12px inner gap, 48px vertical padding, title 20px/medium,
 * description 14px secondary capped at 440px. Tokens only.
 */
export default css`
  display: flex;
  flex-direction: column;
  gap: ${spacing(5)};
  flex: 1;
  min-height: 0;

  .vendors-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
  }

  /* Prototype's container chain is flex-1 all the way down (the page shell
     is h-screen; the bordered card and its contents inherit that height) —
     the card fills the remaining page height, it doesn't shrink-wrap to
     whatever's inside it. Box is a plain div with no built-in stretch
     behavior, so this page has to establish it explicitly via Box's own
     css override slot. */
  .vendors-box {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    width: 100%;
  }

  /* Prototype: text-[32px] font-medium leading-[36px] — the page's own H1
     title (PageHeader), NOT the same 20px/24px treatment as the empty-state
     title below. Transcribe each element's own declared value; don't reuse
     a nearby one just because it's also a "title." */
  .vendors-title {
    font-size: 32px;
    line-height: 36px;
    font-family: ${font_family_weight_medium};
    color: ${text_colorTextPrimary};
    margin: 0;
  }

  .vendors-error {
    padding: ${spacing(6)};
  }

  .vendors-empty {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${spacing(5)};
    padding: ${spacing(12)} 0;
  }

  /* Prototype nests title + description in their own wrapper with a 12px
     gap, separate from the outer icon-to-text-block 20px gap above — two
     different gap values, not one shared gap across all three children. */
  .vendors-empty-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spacing(3)};
    width: 100%;
    max-width: 440px;
    text-align: center;
  }

  .vendors-empty-title {
    font-size: 20px;
    line-height: 24px;
    font-family: ${font_family_weight_medium};
    color: ${text_colorTextPrimary};
    margin: 0;
  }

  .vendors-empty-description {
    ${font_md}
    color: ${text_colorTextSecondary};
    margin: 0;
  }

  .vendors-toolbar {
    display: flex;
    align-items: center;
    gap: ${spacing(6)};
    border-bottom: 1px solid ${base_colorContentBorder};
    background: white;
    padding: ${spacing(4)} ${spacing(6)};
  }

  /* Prototype: text-[20px] text-green-300 (raw palette, not a semantic
     token). Repo lint (EX-39864) bans raw ext-ui palette colors in src/**,
     so this maps to the semantic success-text token instead — same green
     family; confirm the exact shade renders correctly in the browser
     (computed-style verify pass), not just from this token name. */
  .vendors-count {
    white-space: nowrap;
    font-size: 20px;
    color: ${text_colorTextSuccess};
  }

  /* Pagination footer. Prototype declared values: px-[24px] py-[12px],
     text-[14px], gap-[16px], chevrons size-[16px], disabled opacity-40,
     text grey-700, top border grey-400. Token mappings (both verified in the
     browser): grey-700 -> text_colorTextSecondary, grey-400 ->
     base_colorContentBorder. Right-aligned, pushed to the bottom of the card
     so it reads as the table's footer even on tall viewports. */
  .vendors-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${spacing(4)};
    padding: ${spacing(3)} ${spacing(6)};
    margin-top: auto;
    border-top: 1px solid ${base_colorContentBorder};
    ${font_md}
    color: ${text_colorTextSecondary};
  }

  .vendors-footer-rows-per-page,
  .vendors-footer-range {
    white-space: nowrap;
  }

  .vendors-footer-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: ${spacing(1)};
    border: none;
    border-radius: ${spacing(1)};
    background: none;
    color: inherit;
    cursor: pointer;
  }

  /* Genuine disabled attribute drives this — not decoration; the button is
     non-operable when disabled, opacity is only the visual echo. */
  .vendors-footer-button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`
